/**
 * KOPS-577: Agent Deal Board — Kanban pipeline for agent deals
 * Mirrors lender KanbanDealBoard with agent-specific stages.
 * Includes "Share with Lender" feature for deal linking.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone, faEnvelope, faStickyNote, faClock, faChevronDown, faChevronRight,
  faSpinner, faGripVertical, faSearch, faSyncAlt, faFire, faHandshake,
  faCheckCircle, faClipboardList, faHome, faTrophy, faUser, faTimes,
  faPlus, faShareAlt, faPaperPlane, faDollarSign, faCalendarCheck,
  faMapMarkerAlt, faArrowRight, faLink, faExternalLinkAlt, faEdit, faSave,
  faUserTie,
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';
import toast from 'react-hot-toast';

// ---------------------------------------------------------------------------
// Colors (navy + gold)
// ---------------------------------------------------------------------------
const C = {
  navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
  gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
  white: '#FFFFFF', off: '#E8EEF6', muted: '#8FA3BF',
  green: '#27ae60', red: '#e74c3c', orange: '#f39c12', blue: '#3498db',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface AgentDealCard {
  id: string;
  leadId?: string;
  dealName: string;
  clientName: string;
  clientFirstName: string;
  clientLastName: string;
  phone: string;
  email: string;
  city: string;
  state: string;
  address: string;
  dealType: string;
  dealPrice: number | null;
  commissionValue: number | null;
  projectedCloseDate: string | null;
  daysInStage: number;
  linkedDealId: string | null;
  sharedAt: string | null;
  loanType: string;
  notes: string;
  stageUpdatedAt: string;
  createdAt: string;
}

interface StageConfig {
  key: string;
  label: string;
  color: string;
  icon: any;
}

interface LinkedLender {
  relationshipId: string;
  lenderUserId: string;
  name: string;
  email: string;
  company: string;
}

// ---------------------------------------------------------------------------
// Agent pipeline stages
// ---------------------------------------------------------------------------
const STAGES: StageConfig[] = [
  { key: 'new_lead',        label: 'New Lead',          color: '#e74c3c', icon: faFire },
  { key: 'contacted',       label: 'Contacted',         color: '#e67e22', icon: faPhone },
  { key: 'showing_meeting', label: 'Showing / Meeting', color: '#f1c40f', icon: faHome },
  { key: 'offer_submitted', label: 'Offer Submitted',   color: '#3498db', icon: faClipboardList },
  { key: 'under_contract',  label: 'Under Contract',    color: '#27ae60', icon: faHandshake },
  { key: 'closing',         label: 'Closing',           color: '#8e44ad', icon: faCheckCircle },
  { key: 'closed',          label: 'Closed',            color: '#2ecc71', icon: faTrophy },
];

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const AgentDealBoard: React.FC = () => {
  const [stages, setStages] = useState<Record<string, AgentDealCard[]>>({});
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Create deal modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<any>({});
  const [creating, setCreating] = useState(false);

  // Detail modal
  const [detailCard, setDetailCard] = useState<AgentDealCard | null>(null);
  const [detailData, setDetailData] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Share modal
  const [shareCard, setShareCard] = useState<AgentDealCard | null>(null);
  const [lenders, setLenders] = useState<LinkedLender[]>([]);
  const [lendersLoaded, setLendersLoaded] = useState(false);
  const [selectedLender, setSelectedLender] = useState('');
  const [shareNotes, setShareNotes] = useState('');
  const [sharePhone, setSharePhone] = useState(false);
  const [shareEmail, setShareEmail] = useState(false);
  const [shareAddress, setShareAddress] = useState(false);
  const [sharing, setSharing] = useState(false);

  // Edit mode in detail
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [saving, setSaving] = useState(false);

  // ─── Fetch board ───
  const fetchBoard = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/agent/deals');
      if (res.data?.success) {
        setStages(res.data.stages || {});
        setCounts(res.data.counts || {});
      }
    } catch (err) {
      console.error('[AgentDealBoard] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  // ─── Fetch lenders ───
  const fetchLenders = useCallback(async () => {
    if (lendersLoaded) return;
    try {
      const res = await axiosInstance.get('/agent/lenders');
      if (res.data?.success) {
        setLenders(res.data.lenders || []);
        setLendersLoaded(true);
      }
    } catch {}
  }, [lendersLoaded]);

  // ─── Drag & Drop ───
  const handleDragEnd = useCallback(async (result: DropResult) => {
    if (!result.destination) return;
    const from = result.source.droppableId;
    const to = result.destination.droppableId;
    if (from === to) return;

    const cardId = result.draggableId;
    const card = stages[from]?.find(c => c.id === cardId);
    if (!card) return;

    // Optimistic update
    setStages(prev => {
      const next = { ...prev };
      next[from] = (next[from] || []).filter(c => c.id !== cardId);
      next[to] = [card, ...(next[to] || [])];
      return next;
    });
    setCounts(prev => ({
      ...prev,
      [from]: Math.max(0, (prev[from] || 0) - 1),
      [to]: (prev[to] || 0) + 1,
    }));

    try {
      await axiosInstance.patch(`/agent/deals/${cardId}`, { stage: to });
      toast.success(`Moved to ${STAGES.find(s => s.key === to)?.label || to}`);
    } catch {
      toast.error('Failed to update stage');
      fetchBoard();
    }
  }, [stages, fetchBoard]);

  // ─── Create deal ───
  const handleCreate = useCallback(async () => {
    if (!createForm.clientFirstName && !createForm.dealName) {
      toast.error('Client name or deal name required');
      return;
    }
    setCreating(true);
    try {
      const res = await axiosInstance.post('/agent/deals', createForm);
      if (res.data?.success) {
        toast.success('Deal created');
        setShowCreate(false);
        setCreateForm({});
        fetchBoard();
      }
    } catch {
      toast.error('Failed to create deal');
    } finally {
      setCreating(false);
    }
  }, [createForm, fetchBoard]);

  // ─── Deal detail ───
  const openDetail = useCallback(async (card: AgentDealCard) => {
    setDetailCard(card);
    setDetailLoading(true);
    setEditing(false);
    try {
      const res = await axiosInstance.get(`/agent/deals/${card.id}`);
      if (res.data?.success) {
        setDetailData(res.data.deal);
        setEditForm(res.data.deal);
      }
    } catch {
      toast.error('Failed to load deal details');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // ─── Save edit ───
  const handleSaveEdit = useCallback(async () => {
    if (!detailCard) return;
    setSaving(true);
    try {
      await axiosInstance.patch(`/agent/deals/${detailCard.id}`, editForm);
      toast.success('Deal updated');
      setEditing(false);
      openDetail(detailCard);
      fetchBoard();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }, [detailCard, editForm, openDetail, fetchBoard]);

  // ─── Share with lender ───
  const generateShareSummary = useCallback((card: AgentDealCard): string => {
    const parts: string[] = [];
    const typeLabel = card.dealType ? card.dealType.charAt(0).toUpperCase() + card.dealType.slice(1) : '';
    const priceStr = card.dealPrice ? `$${(card.dealPrice / 1000).toFixed(0)}K` : '';
    const location = [card.city, card.state].filter(Boolean).join(', ');
    const stageLabel = STAGES.find(s => s.key === (stages && Object.entries(stages).find(([_, cards]) => cards.some(c => c.id === card.id))?.[0]))?.label || '';

    // Build natural summary
    if (typeLabel && priceStr && location) {
      parts.push(`${typeLabel}, ${priceStr} in ${location}.`);
    } else if (typeLabel && priceStr) {
      parts.push(`${typeLabel}, ${priceStr}.`);
    } else if (typeLabel) {
      parts.push(`${typeLabel} deal.`);
    }

    if (card.loanType) parts.push(`${card.loanType.charAt(0).toUpperCase() + card.loanType.slice(1)} loan.`);
    if (stageLabel) parts.push(`Currently: ${stageLabel}.`);
    if (card.notes) {
      // Take first sentence or first 120 chars of agent notes
      const firstSentence = card.notes.split(/\.\s/)[0];
      parts.push(firstSentence.length > 120 ? firstSentence.substring(0, 117) + '...' : firstSentence + (firstSentence.endsWith('.') ? '' : '.'));
    }

    return parts.join(' ');
  }, [stages]);

  const openShareModal = useCallback((card: AgentDealCard) => {
    setShareCard(card);
    setSelectedLender('');
    setShareNotes(generateShareSummary(card));
    setSharePhone(false);
    setShareEmail(false);
    setShareAddress(false);
    fetchLenders();
  }, [fetchLenders, generateShareSummary]);

  const handleShare = useCallback(async () => {
    if (!shareCard || !selectedLender) {
      toast.error('Select a lender');
      return;
    }
    setSharing(true);
    try {
      const res = await axiosInstance.post(`/agent/deals/${shareCard.id}/share`, {
        lenderUserId: selectedLender,
        notes: shareNotes,
        sharePhone,
        shareEmail,
        shareAddress,
      });
      if (res.data?.success) {
        toast.success('Deal shared with lender!');
        setShareCard(null);
        fetchBoard();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to share deal');
    } finally {
      setSharing(false);
    }
  }, [shareCard, selectedLender, shareNotes, sharePhone, shareEmail, shareAddress, fetchBoard]);

  // ─── Filter ───
  const filteredStages = useMemo(() => {
    if (!search.trim()) return stages;
    const q = search.toLowerCase();
    const filtered: Record<string, AgentDealCard[]> = {};
    for (const [stage, cards] of Object.entries(stages)) {
      filtered[stage] = cards.filter(c =>
        c.clientName.toLowerCase().includes(q) ||
        c.dealName.toLowerCase().includes(q) ||
        c.phone.includes(q) ||
        (c.city || '').toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [stages, search]);

  // ─── Format helpers ───
  const fmt$ = (v: number | null) => v ? `$${v.toLocaleString()}` : '—';
  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString() : '—';

  // ─── Loading ───
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400, color: C.gold }}>
        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
        <span style={{ marginLeft: 12, fontSize: 16 }}>Loading Deal Board...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 8px' }}>
      {/* ─── Header ─── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16, flexWrap: 'wrap', gap: 8
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h3 style={{ margin: 0, color: C.white, fontSize: 18 }}>
            <FontAwesomeIcon icon={faClipboardList} style={{ color: C.gold, marginRight: 8 }} />
            My Deal Board
          </h3>
          <span style={{
            background: C.gold, color: C.navy, borderRadius: 12,
            padding: '2px 10px', fontSize: 13, fontWeight: 700
          }}>
            {Object.values(counts).reduce((a, b) => a + b, 0)} deals
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <FontAwesomeIcon icon={faSearch} style={{
              position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
              color: C.muted, fontSize: 13
            }} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search deals..."
              style={{
                background: C.navy2, border: `1px solid ${C.navy3}`, borderRadius: 6,
                color: C.white, padding: '6px 10px 6px 30px', fontSize: 13, width: 180,
                outline: 'none',
              }}
            />
          </div>
          <button onClick={() => { setCreateForm({}); setShowCreate(true); }} style={{
            background: C.gold, color: C.navy, border: 'none', borderRadius: 6,
            padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6
          }}>
            <FontAwesomeIcon icon={faPlus} /> New Deal
          </button>
          <button onClick={() => { setLoading(true); fetchBoard(); }} style={{
            background: 'transparent', border: `1px solid ${C.navy3}`, borderRadius: 6,
            color: C.muted, padding: '6px 10px', cursor: 'pointer', fontSize: 13,
          }}>
            <FontAwesomeIcon icon={faSyncAlt} />
          </button>
        </div>
      </div>

      {/* ─── Kanban Columns ─── */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div style={{
          display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 16,
          minHeight: 400,
        }}>
          {STAGES.map(stage => (
            <KanbanColumn
              key={stage.key}
              stage={stage}
              cards={filteredStages[stage.key] || []}
              count={counts[stage.key] || 0}
              onCardClick={openDetail}
              onShare={openShareModal}
              hasLenders={lenders.length > 0 || !lendersLoaded}
            />
          ))}
        </div>
      </DragDropContext>

      {/* ─── Create Deal Modal ─── */}
      {showCreate && (
        <ModalOverlay onClose={() => setShowCreate(false)}>
          <div style={{ width: 480 }}>
            <h4 style={{ color: C.gold, margin: '0 0 16px' }}>
              <FontAwesomeIcon icon={faPlus} /> Create New Deal
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <FormField label="First Name" value={createForm.clientFirstName || ''} onChange={v => setCreateForm((p: any) => ({ ...p, clientFirstName: v }))} />
              <FormField label="Last Name" value={createForm.clientLastName || ''} onChange={v => setCreateForm((p: any) => ({ ...p, clientLastName: v }))} />
              <FormField label="Phone" value={createForm.clientPhone || ''} onChange={v => setCreateForm((p: any) => ({ ...p, clientPhone: v }))} />
              <FormField label="Email" value={createForm.clientEmail || ''} onChange={v => setCreateForm((p: any) => ({ ...p, clientEmail: v }))} />
              <FormField label="Property Address" value={createForm.propertyAddress || ''} onChange={v => setCreateForm((p: any) => ({ ...p, propertyAddress: v }))} full />
              <FormField label="City" value={createForm.propertyCity || ''} onChange={v => setCreateForm((p: any) => ({ ...p, propertyCity: v }))} />
              <FormField label="State" value={createForm.propertyState || ''} onChange={v => setCreateForm((p: any) => ({ ...p, propertyState: v }))} />
              <FormSelect label="Deal Type" value={createForm.dealType || ''} onChange={v => setCreateForm((p: any) => ({ ...p, dealType: v }))}
                options={[['', 'Select...'], ['purchase', 'Purchase'], ['listing', 'Listing'], ['lease', 'Lease'], ['referral', 'Referral']]} />
              <FormField label="Price" value={createForm.dealPrice || ''} onChange={v => setCreateForm((p: any) => ({ ...p, dealPrice: v }))} type="number" />
              <FormField label="Commission" value={createForm.commissionValue || ''} onChange={v => setCreateForm((p: any) => ({ ...p, commissionValue: v }))} type="number" />
              <FormField label="Projected Close" value={createForm.projectedCloseDate || ''} onChange={v => setCreateForm((p: any) => ({ ...p, projectedCloseDate: v }))} type="date" />
              <FormSelect label="Loan Type" value={createForm.loanType || ''} onChange={v => setCreateForm((p: any) => ({ ...p, loanType: v }))}
                options={[['', 'Select...'], ['conventional', 'Conventional'], ['fha', 'FHA'], ['va', 'VA'], ['usda', 'USDA'], ['jumbo', 'Jumbo']]} />
            </div>
            <FormField label="Notes" value={createForm.notes || ''} onChange={v => setCreateForm((p: any) => ({ ...p, notes: v }))} full textarea />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={() => setShowCreate(false)} style={btnSecondary}>Cancel</button>
              <button onClick={handleCreate} disabled={creating} style={btnPrimary}>
                {creating ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlus} />}
                {' '}Create Deal
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ─── Deal Detail Modal ─── */}
      {detailCard && (
        <ModalOverlay onClose={() => { setDetailCard(null); setDetailData(null); setEditing(false); }}>
          <div style={{ width: 560, maxHeight: '80vh', overflowY: 'auto' }}>
            {detailLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: C.gold }}>
                <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              </div>
            ) : detailData ? (
              <>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <h4 style={{ color: C.white, margin: '0 0 4px' }}>
                      <a
                        href={`${window.location.pathname.includes('lender-portal') ? '/lender-portal' : '/agent-portal'}/clients?id=${detailCard.leadId || detailCard.id}&tab=overview`}
                        style={{ color: C.white, textDecoration: 'none', borderBottom: `1px dashed ${C.gold}40` }}
                        onMouseEnter={e => (e.currentTarget.style.color = C.gold)}
                        onMouseLeave={e => (e.currentTarget.style.color = C.white)}
                        title="View client profile"
                      >
                        {detailData.clientFirstName} {detailData.clientLastName}
                      </a>
                    </h4>
                    <span style={{ color: C.muted, fontSize: 13 }}>{detailData.dealName}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {!detailCard.linkedDealId && (
                      <button onClick={() => openShareModal(detailCard)} style={{
                        ...btnPrimary, fontSize: 12, padding: '4px 10px',
                      }}>
                        <FontAwesomeIcon icon={faShareAlt} /> Share with Lender
                      </button>
                    )}
                    <button onClick={() => { setEditing(!editing); setEditForm(detailData); }} style={{
                      ...btnSecondary, fontSize: 12, padding: '4px 10px',
                    }}>
                      <FontAwesomeIcon icon={editing ? faTimes : faEdit} /> {editing ? 'Cancel' : 'Edit'}
                    </button>
                  </div>
                </div>

                {/* Linked deal badge */}
                {detailData.linkedDeal && (
                  <div style={{
                    background: 'rgba(39,174,96,0.15)', border: '1px solid rgba(39,174,96,0.3)',
                    borderRadius: 6, padding: '8px 12px', marginBottom: 12, fontSize: 13,
                  }}>
                    <FontAwesomeIcon icon={faLink} style={{ color: C.green, marginRight: 6 }} />
                    <span style={{ color: C.green, fontWeight: 600 }}>Shared with Lender</span>
                    <span style={{ color: C.muted, marginLeft: 8 }}>
                      {detailData.linkedDeal.ownerName} — Stage: {detailData.linkedDeal.stage}
                    </span>
                  </div>
                )}

                {/* Stage selector */}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ color: C.muted, fontSize: 12, marginBottom: 4, display: 'block' }}>Stage</label>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {STAGES.map(s => {
                      const current = (editing ? editForm.stage : detailData.stage) === s.key;
                      return (
                        <button
                          key={s.key}
                          onClick={async () => {
                            if (editing) {
                              setEditForm((p: any) => ({ ...p, stage: s.key }));
                            } else {
                              try {
                                await axiosInstance.patch(`/agent/deals/${detailCard.id}`, { stage: s.key });
                                toast.success(`Moved to ${s.label}`);
                                openDetail(detailCard);
                                fetchBoard();
                              } catch { toast.error('Failed'); }
                            }
                          }}
                          style={{
                            background: current ? s.color : 'transparent',
                            color: current ? '#fff' : C.muted,
                            border: `1px solid ${current ? s.color : C.navy3}`,
                            borderRadius: 4, padding: '3px 8px', fontSize: 11,
                            cursor: 'pointer', fontWeight: current ? 700 : 400,
                          }}
                        >
                          <FontAwesomeIcon icon={s.icon} style={{ marginRight: 4 }} />{s.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Fields grid */}
                {editing ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                    <FormField label="First Name" value={editForm.clientFirstName || ''} onChange={v => setEditForm((p: any) => ({ ...p, clientFirstName: v }))} />
                    <FormField label="Last Name" value={editForm.clientLastName || ''} onChange={v => setEditForm((p: any) => ({ ...p, clientLastName: v }))} />
                    <FormField label="Phone" value={editForm.phone || ''} onChange={v => setEditForm((p: any) => ({ ...p, clientPhone: v }))} />
                    <FormField label="Email" value={editForm.email || ''} onChange={v => setEditForm((p: any) => ({ ...p, clientEmail: v }))} />
                    <FormField label="Address" value={editForm.address || ''} onChange={v => setEditForm((p: any) => ({ ...p, propertyAddress: v }))} full />
                    <FormField label="City" value={editForm.city || ''} onChange={v => setEditForm((p: any) => ({ ...p, propertyCity: v }))} />
                    <FormField label="State" value={editForm.state || ''} onChange={v => setEditForm((p: any) => ({ ...p, propertyState: v }))} />
                    <FormField label="Price" value={editForm.dealPrice || ''} onChange={v => setEditForm((p: any) => ({ ...p, dealPrice: v }))} type="number" />
                    <FormField label="Commission" value={editForm.commissionValue || ''} onChange={v => setEditForm((p: any) => ({ ...p, commissionValue: v }))} type="number" />
                    <FormField label="Close Date" value={editForm.projectedCloseDate?.split('T')[0] || ''} onChange={v => setEditForm((p: any) => ({ ...p, projectedCloseDate: v }))} type="date" />
                    <FormField label="Notes" value={editForm.notes || ''} onChange={v => setEditForm((p: any) => ({ ...p, notes: v }))} full textarea />
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: 16 }}>
                    <DetailRow icon={faUser} label="Client" value={`${detailData.clientFirstName} ${detailData.clientLastName}`} />
                    <DetailRow icon={faPhone} label="Phone" value={detailData.phone} link={detailData.phone ? `tel:${detailData.phone}` : undefined} />
                    <DetailRow icon={faEnvelope} label="Email" value={detailData.email} link={detailData.email ? `mailto:${detailData.email}` : undefined} />
                    <DetailRow icon={faMapMarkerAlt} label="Location" value={[detailData.address, detailData.city, detailData.state].filter(Boolean).join(', ')} />
                    <DetailRow icon={faHome} label="Type" value={detailData.dealType} />
                    <DetailRow icon={faDollarSign} label="Price" value={fmt$(detailData.dealPrice)} />
                    <DetailRow icon={faDollarSign} label="Commission" value={fmt$(detailData.commissionValue)} />
                    <DetailRow icon={faCalendarCheck} label="Close Date" value={fmtDate(detailData.projectedCloseDate)} />
                    <DetailRow icon={faClock} label="Days in Stage" value={`${detailData.daysInStage}d`} />
                    {detailData.loanType && <DetailRow icon={faClipboardList} label="Loan" value={detailData.loanType} />}
                  </div>
                )}

                {/* Notes */}
                {!editing && detailData.notes && (
                  <div style={{
                    background: C.navy2, borderRadius: 6, padding: 12, marginBottom: 16,
                    border: `1px solid ${C.navy3}`,
                  }}>
                    <label style={{ color: C.muted, fontSize: 11, display: 'block', marginBottom: 4 }}>Notes</label>
                    <p style={{ color: C.white, fontSize: 13, margin: 0, whiteSpace: 'pre-wrap' }}>{detailData.notes}</p>
                  </div>
                )}

                {/* Footer */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  {editing && (
                    <button onClick={handleSaveEdit} disabled={saving} style={btnPrimary}>
                      {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSave} />}
                      {' '}Save Changes
                    </button>
                  )}
                  <button onClick={() => { setDetailCard(null); setDetailData(null); setEditing(false); }} style={btnSecondary}>
                    Close
                  </button>
                </div>
              </>
            ) : (
              <p style={{ color: C.muted }}>Failed to load deal details.</p>
            )}
          </div>
        </ModalOverlay>
      )}

      {/* ─── Share with Lender Modal ─── */}
      {shareCard && (
        <ModalOverlay onClose={() => setShareCard(null)}>
          <div style={{ width: 440 }}>
            <h4 style={{ color: C.gold, margin: '0 0 4px' }}>
              <FontAwesomeIcon icon={faShareAlt} /> Share Deal with Lender
            </h4>
            <p style={{ color: C.muted, fontSize: 13, margin: '0 0 16px' }}>
              {shareCard.clientName || shareCard.dealName}
            </p>

            {lenders.length === 0 ? (
              <div style={{
                background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)',
                borderRadius: 6, padding: 16, textAlign: 'center', color: C.muted, fontSize: 13,
              }}>
                <FontAwesomeIcon icon={faUserTie} style={{ fontSize: 24, color: C.red, marginBottom: 8, display: 'block' }} />
                No linked lenders found. Ask your lender partner to send you an invitation.
              </div>
            ) : (
              <>
                <FormSelect label="Select Lender" value={selectedLender} onChange={setSelectedLender}
                  options={[['', 'Choose lender...'], ...lenders.map(l => [l.lenderUserId, `${l.name}${l.company ? ` (${l.company})` : ''}`])]} full />

                <div style={{ margin: '12px 0' }}>
                  <label style={{ color: C.muted, fontSize: 12, display: 'block', marginBottom: 6 }}>
                    What to share with lender (client name always shared):
                  </label>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <CheckboxField label="Phone" checked={sharePhone} onChange={setSharePhone} />
                    <CheckboxField label="Email" checked={shareEmail} onChange={setShareEmail} />
                    <CheckboxField label="Address" checked={shareAddress} onChange={setShareAddress} />
                  </div>
                </div>

                <FormField label="Summary for lender (auto-generated — edit if needed)" value={shareNotes} onChange={setShareNotes} full textarea />

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  <button onClick={() => setShareCard(null)} style={btnSecondary}>Cancel</button>
                  <button onClick={handleShare} disabled={sharing || !selectedLender} style={btnPrimary}>
                    {sharing ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPaperPlane} />}
                    {' '}Share Deal
                  </button>
                </div>
              </>
            )}
          </div>
        </ModalOverlay>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Kanban Column
// ---------------------------------------------------------------------------
const KanbanColumn: React.FC<{
  stage: StageConfig;
  cards: AgentDealCard[];
  count: number;
  onCardClick: (c: AgentDealCard) => void;
  onShare: (c: AgentDealCard) => void;
  hasLenders: boolean;
}> = ({ stage, cards, count, onCardClick, onShare, hasLenders }) => (
  <div style={{ minWidth: 220, maxWidth: 260, flex: '1 0 220px' }}>
    {/* Column Header */}
    <div style={{
      background: C.navy2, borderRadius: '8px 8px 0 0', padding: '8px 12px',
      display: 'flex', alignItems: 'center', gap: 8,
      borderBottom: `3px solid ${stage.color}`,
    }}>
      <FontAwesomeIcon icon={stage.icon} style={{ color: stage.color, fontSize: 13 }} />
      <span style={{ color: C.white, fontSize: 13, fontWeight: 600, flex: 1 }}>{stage.label}</span>
      <span style={{
        background: stage.color + '30', color: stage.color, borderRadius: 10,
        padding: '1px 7px', fontSize: 11, fontWeight: 700,
      }}>{count}</span>
    </div>

    {/* Droppable area */}
    <Droppable droppableId={stage.key}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          style={{
            background: snapshot.isDraggingOver ? `${C.gold}15` : C.navy + 'cc',
            borderRadius: '0 0 8px 8px',
            padding: 6, minHeight: 120,
            transition: 'background 0.2s',
          }}
        >
          {cards.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px 8px', color: C.muted, fontSize: 12 }}>
              No deals
            </div>
          )}
          {cards.map((card, idx) => (
            <Draggable key={card.id} draggableId={card.id} index={idx}>
              {(prov, snap) => (
                <div
                  ref={prov.innerRef}
                  {...prov.draggableProps}
                  {...prov.dragHandleProps}
                  onClick={() => onCardClick(card)}
                  style={{
                    background: snap.isDragging ? C.navy3 : C.navy2,
                    borderRadius: 6, padding: '8px 10px', marginBottom: 6,
                    border: `1px solid ${snap.isDragging ? C.gold : C.navy3}`,
                    cursor: 'pointer',
                    ...prov.draggableProps.style,
                  }}
                >
                  {/* Client name */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <FontAwesomeIcon icon={faGripVertical} style={{ color: C.muted, fontSize: 10 }} />
                    <span style={{ color: C.white, fontSize: 13, fontWeight: 600, flex: 1 }}>
                      {card.clientName || card.dealName}
                    </span>
                  </div>

                  {/* Deal type + price badges */}
                  <div style={{ display: 'flex', gap: 4, marginBottom: 4, flexWrap: 'wrap' }}>
                    {card.dealType && (
                      <span style={{
                        background: C.blue + '25', color: C.blue, fontSize: 10,
                        padding: '1px 6px', borderRadius: 3, fontWeight: 600,
                      }}>{card.dealType}</span>
                    )}
                    {card.dealPrice && (
                      <span style={{
                        background: C.green + '25', color: C.green, fontSize: 10,
                        padding: '1px 6px', borderRadius: 3, fontWeight: 600,
                      }}>${(card.dealPrice / 1000).toFixed(0)}K</span>
                    )}
                    {card.loanType && (
                      <span style={{
                        background: C.orange + '25', color: C.orange, fontSize: 10,
                        padding: '1px 6px', borderRadius: 3, fontWeight: 600,
                      }}>{card.loanType}</span>
                    )}
                  </div>

                  {/* Location */}
                  {card.city && (
                    <div style={{ color: C.muted, fontSize: 11, marginBottom: 3 }}>
                      <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: 4, fontSize: 10 }} />
                      {card.city}{card.state ? `, ${card.state}` : ''}
                    </div>
                  )}

                  {/* Close date */}
                  {card.projectedCloseDate && (
                    <div style={{ color: C.muted, fontSize: 11, marginBottom: 3 }}>
                      <FontAwesomeIcon icon={faCalendarCheck} style={{ marginRight: 4, fontSize: 10 }} />
                      Close: {fmtDateShort(card.projectedCloseDate)}
                    </div>
                  )}

                  {/* Footer: days in stage + share + contact */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
                    <span style={{
                      color: card.daysInStage > 14 ? C.red : card.daysInStage > 7 ? C.orange : C.muted,
                      fontSize: 10, fontWeight: 600,
                    }}>
                      <FontAwesomeIcon icon={faClock} style={{ marginRight: 3 }} />
                      {card.daysInStage}d
                    </span>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {card.linkedDealId ? (
                        <span style={{ color: C.green, fontSize: 10 }} title="Shared with lender">
                          <FontAwesomeIcon icon={faLink} />
                        </span>
                      ) : hasLenders ? (
                        <span
                          onClick={e => { e.stopPropagation(); onShare(card); }}
                          style={{ color: C.gold, fontSize: 10, cursor: 'pointer' }}
                          title="Share with lender"
                        >
                          <FontAwesomeIcon icon={faShareAlt} />
                        </span>
                      ) : null}
                      {card.phone && (
                        <a href={`tel:${card.phone}`} onClick={e => e.stopPropagation()} style={{ color: C.green, fontSize: 10 }}>
                          <FontAwesomeIcon icon={faPhone} />
                        </a>
                      )}
                      {card.email && (
                        <a href={`mailto:${card.email}`} onClick={e => e.stopPropagation()} style={{ color: C.blue, fontSize: 10 }}>
                          <FontAwesomeIcon icon={faEnvelope} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  </div>
);

// ---------------------------------------------------------------------------
// Helper Components
// ---------------------------------------------------------------------------
const fmtDateShort = (d: string) => {
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch { return d; }
};

const ModalOverlay: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({ onClose, children }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 9999,
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      padding: 20,
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: C.navy, border: `1px solid ${C.navy3}`,
        borderRadius: 10, padding: 24,
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}
    >
      {children}
    </div>
  </div>
);

const DetailRow: React.FC<{ icon: any; label: string; value: string; link?: string }> = ({ icon, label, value, link }) => (
  <div style={{ marginBottom: 2 }}>
    <div style={{ color: C.muted, fontSize: 11 }}>
      <FontAwesomeIcon icon={icon} style={{ marginRight: 4, fontSize: 10 }} />{label}
    </div>
    {link ? (
      <a href={link} style={{ color: C.gold, fontSize: 13, textDecoration: 'none' }}>{value || '—'}</a>
    ) : (
      <div style={{ color: C.white, fontSize: 13 }}>{value || '—'}</div>
    )}
  </div>
);

const FormField: React.FC<{
  label: string; value: string; onChange: (v: string) => void;
  type?: string; full?: boolean; textarea?: boolean;
}> = ({ label, value, onChange, type = 'text', full, textarea }) => (
  <div style={{ gridColumn: full ? '1 / -1' : undefined }}>
    <label style={{ color: C.muted, fontSize: 11, display: 'block', marginBottom: 2 }}>{label}</label>
    {textarea ? (
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={3}
        style={{
          width: '100%', background: C.navy2, border: `1px solid ${C.navy3}`,
          borderRadius: 4, color: C.white, padding: '6px 8px', fontSize: 13,
          outline: 'none', resize: 'vertical',
        }}
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%', background: C.navy2, border: `1px solid ${C.navy3}`,
          borderRadius: 4, color: C.white, padding: '6px 8px', fontSize: 13,
          outline: 'none',
        }}
      />
    )}
  </div>
);

const FormSelect: React.FC<{
  label: string; value: string; onChange: (v: string) => void;
  options: string[][]; full?: boolean;
}> = ({ label, value, onChange, options, full }) => (
  <div style={{ gridColumn: full ? '1 / -1' : undefined }}>
    <label style={{ color: C.muted, fontSize: 11, display: 'block', marginBottom: 2 }}>{label}</label>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        width: '100%', background: C.navy2, border: `1px solid ${C.navy3}`,
        borderRadius: 4, color: C.white, padding: '6px 8px', fontSize: 13,
        outline: 'none',
      }}
    >
      {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
    </select>
  </div>
);

const CheckboxField: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = ({ label, checked, onChange }) => (
  <label style={{ color: C.white, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
    {label}
  </label>
);

// ---------------------------------------------------------------------------
// Button styles
// ---------------------------------------------------------------------------
const btnPrimary: React.CSSProperties = {
  background: C.gold, color: C.navy, border: 'none', borderRadius: 6,
  padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  display: 'flex', alignItems: 'center', gap: 6,
};

const btnSecondary: React.CSSProperties = {
  background: 'transparent', color: C.muted, border: `1px solid ${C.navy3}`,
  borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer',
};

export default AgentDealBoard;
